import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="clubs-map"
export default class extends Controller {
  static targets = ["map", "locationInput", "searchResults"]
  static values = {
    clubs: Array,
    url: String
  }

  connect() {
    try {
      this.initializeMap()
      this.loadClubs()
    } catch (error) {
      console.error('Error initializing clubs map:', error)
      this.updateSearchResults('Error initializing map')
    }
  }

  initializeMap() {
    // Initialize map centered on US
    this.map = L.map(this.mapTarget).setView([39.8283, -98.5795], 4)

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    this.markers = []
  }

  async loadClubs(latitude = null, longitude = null, location = null) {
    let url = this.hasUrlValue ? this.urlValue : '/rope-clubs-directory.json'
    const params = new URLSearchParams()

    if (latitude && longitude) {
      params.append('latitude', latitude)
      params.append('longitude', longitude)
    } else if (location) {
      params.append('location', location)
    }

    if (params.toString()) {
      url += '?' + params.toString()
    }

    try {
      const response = await fetch(url)
      const data = await response.json()

      // Handle both old format (array) and new format (object with clubs array)
      const clubs = Array.isArray(data) ? data : data.clubs

      this.displayClubs(clubs)

      // Zoom to show clubs appropriately
      if (clubs.length > 0) {
        this.fitMapToBounds(clubs)
      } else {
        // No clubs found, zoom to US
        this.map.setView([39.8283, -98.5795], 4)
      }
    } catch (error) {
      console.error('Error loading clubs:', error)
      this.updateSearchResults('Error loading clubs')
    }
  }

  displayClubs(clubs) {
    // Clear existing markers
    this.markers.forEach(marker => marker.remove())
    this.markers = []

    // Group clubs by coordinates to detect overlaps
    const clubsByLocation = {}
    clubs.forEach(club => {
      if (club.latitude && club.longitude) {
        const key = `${club.latitude},${club.longitude}`
        if (!clubsByLocation[key]) {
          clubsByLocation[key] = []
        }
        clubsByLocation[key].push(club)
      }
    })

    // Add markers with offset for overlapping locations
    Object.values(clubsByLocation).forEach(clubsAtLocation => {
      clubsAtLocation.forEach((club, index) => {
        let lat = club.latitude
        let lng = club.longitude

        // If multiple clubs at same location, offset them in a circle
        if (clubsAtLocation.length > 1) {
          const offset = 0.001 // Small offset (roughly 100 meters)
          const angle = (index / clubsAtLocation.length) * 2 * Math.PI
          lat += offset * Math.cos(angle)
          lng += offset * Math.sin(angle)
        }

        const marker = L.marker([lat, lng]).addTo(this.map)

        const websiteButton = club.website ? `<a href="${this.escapeHtml(club.website)}" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">Visit Website</a>` : ''
        const instagramButton = club.instagram ? `<a href="https://instagram.com/${this.escapeHtml(club.instagram)}" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">Instagram</a>` : ''
        const buttons = [websiteButton, instagramButton].filter(btn => btn).join(' ')

        const popupContent = `
          <div class="club-popup">
            <h5>${this.escapeHtml(club.name)}</h5>
            ${club.description ? `<p>${this.escapeHtml(club.description)}</p>` : ''}
            <p class="mb-1">
              <strong>Location:</strong><br>
              ${this.escapeHtml(club.city)}, ${this.escapeHtml(club.state)} ${this.escapeHtml(club.zip || '')}
            </p>
            ${buttons ? `<p class="mb-0">${buttons}</p>` : ''}
          </div>
        `

        marker.bindPopup(popupContent)
        this.markers.push(marker)
      })
    })

    this.updateSearchResults(`Found ${clubs.length} club${clubs.length !== 1 ? 's' : ''}`)
  }

  updateSearchResults(message) {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.textContent = message
    }
  }

  fitMapToBounds(clubs) {
    const bounds = clubs
      .filter(club => club.latitude && club.longitude)
      .map(club => [club.latitude, club.longitude])

    if (bounds.length > 0) {
      this.map.fitBounds(bounds, { padding: [50, 50] })
    }
  }

  useMyLocation(event) {
    event.preventDefault()

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser')
      return
    }

    const button = event.target
    const originalText = button.textContent
    button.textContent = 'Getting location...'
    button.disabled = true

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude
        const longitude = position.coords.longitude

        this.map.setView([latitude, longitude], 10)
        this.loadClubs(latitude, longitude)

        // Add a marker for user's location
        L.marker([latitude, longitude], {
          icon: L.icon({
            iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSI0MSIgdmlld0JveD0iMCAwIDI1IDQxIj48cGF0aCBmaWxsPSIjMDA3YmZmIiBkPSJNMTIuNSAwQzUuNiAwIDAgNS42IDAgMTIuNWMwIDEuOC40IDMuNSAxLjEgNS4xTDEyLjUgNDEgMjMuOSAxNy42Yy43LTEuNiAxLjEtMy4zIDEuMS01LjFDMjUgNS42IDE5LjQgMCAxMi41IDB6bTAgMTcuNWMtMi44IDAtNS0yLjItNS01czIuMi01IDUtNSA1IDIuMiA1IDUtMi4yIDUtNSA1eiIvPjwvc3ZnPg==',
            iconSize: [25, 41],
            iconAnchor: [12, 41]
          })
        }).addTo(this.map).bindPopup('Your Location')

        button.textContent = originalText
        button.disabled = false
      },
      (error) => {
        alert('Unable to retrieve your location: ' + error.message)
        button.textContent = originalText
        button.disabled = false
      }
    )
  }

  searchByLocation(event) {
    event.preventDefault()
    const location = this.locationInputTarget.value.trim()

    if (location) {
      this.loadClubs(null, null, location)
    } else {
      this.loadClubs()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
